import styled from '@emotion/styled';

import { useEffect, useState } from 'react';

import COLOR from '../../../../../../shared/constant/colorset';
import SNSButton from './SNSButton';
import useShareProduct from '../../../../hooks/useShareProduct';
import Alert from '../../../../../../shared/components/Alert/Alert';
import { branchService } from '../../../../../../shared/branch';
import { Share } from '../../../../../../shared/branch/events';
import { amplitudeService } from '../../../../../../shared/amplitude';
import {
  ShareProduct,
} from '../../../../../../shared/amplitude/events/product/ShareProduct';
import { useAppSelector } from '../../../../../../shared/store';
import { getFusionQueryId } from '../../../../shared/utils/productDetailEvent';

const Container = styled.div`
  position: absolute;
  right: -5px;
  top: 0;
  width: 238px;
  height: 120px;
  box-shadow: 0 0 20px 0 ${COLOR.shadowGray};
  background-color: #fff;

  &:before {
    content: "";
    position: absolute;
    left: 0;
    top: -10px;
    width: 100%;
    height: 10px;
  }

  &:after {
    content: "";
    position: absolute;
    left: 207px;
    top: -4px;
    width: 12px;
    height: 12px;
    background-color: ${COLOR.kurlyWhite};
    transform: rotate(45deg);
  }
`;

const ShareItemList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  padding: 20px 0 0 21px;
`;

export const ShareItemWrapper = styled.li`
  display: flex;
  width: 113px;

  &:nth-of-type(2) {
    width: 99px;
  }

  &:last-of-type {
    width: 100%;
    padding-top:20px;
  }
`;

const URLInput = styled.input`
  width: 113px;
  height: 30px;
  padding: 7px 0 6px 3px;
  border: none;
  background-color: ${COLOR.btnGray};
  font-size: 11px;
  color: ${COLOR.kurlyGray600};
  letter-spacing: -0.5px;
`;

const LinkCopyButton = styled.button`
  width: 84px;
  height: 30px;
  border: 1px solid ${COLOR.kurlyPurple};
  font-size: 12px;
  color: ${COLOR.kurlyPurple};
  line-height: 28px;
  letter-spacing: -0.5px;
`;

const UrlCheckedImage = styled.img`
  width: 10px;
  height: 9px;
  margin: 10px 0 0 5px;
  vertical-align: top;
`;

interface Props {
  productNo: number;
}

export default function ShareItem({
  productNo,
}: Props) {
  const { queryId } = useAppSelector(({ productList }) => productList);
  const productDetailState = useAppSelector(({ productDetail }) => productDetail);

  const { handleClickSNSButton } = useShareProduct(productNo);

  const [currentUrl, setCurrentUrl] = useState('');
  const [isUrlClipboardCopied, setIsUrlClipboardCopied] = useState(false);

  useEffect(() => {
    const url = window.location.href;
    setCurrentUrl(url);
  }, []);

  const handleClickLinkCopy = () => {
    if (!navigator.clipboard) {
      Alert({ text: 'URL을 복사할 수 없습니다.' });
      return;
    }

    branchService.logEvent(
      new Share({
        description: 'PRODUCT',
      }, [{
        $canonical_identifier: `product/${productNo}`,
        $sku: productNo,
        $product_name: productDetailState.name,
      }]),
    );

    amplitudeService.logEvent(new ShareProduct({
      productDetailState,
      message: 'URL이 클립보드에 복사되었습니다.',
      channel: 'URL 복사',
      fusionQueryId: getFusionQueryId(queryId),
    }));

    navigator.clipboard.writeText(currentUrl)
      .then(() => {
        Alert({ text: 'URL이 클립보드에 복사되었습니다.' });
        setIsUrlClipboardCopied(true);
      }).catch(() => {
        Alert({ text: 'URL을 복사할 수 없습니다.' });
      });
  };

  return (
    <Container>
      <ShareItemList>
        <SNSButton
          name='트윗하기'
          value='twitter'
          imageUrl='https://res.kurly.com/mobile/service/goodsview/1804/ico_twitter.png'
          onClick={() => handleClickSNSButton('twitter')}
        />
        <SNSButton
          name='공유하기'
          value='facebook'
          imageUrl='https://res.kurly.com/mobile/service/goodsview/1804/ico_facebook.png'
          onClick={() => handleClickSNSButton('facebook')}
        />
        <ShareItemWrapper>
          <URLInput
            readOnly
            type="text"
            value={currentUrl}
          />
          <LinkCopyButton onClick={handleClickLinkCopy}>
            URL 복사
            {isUrlClipboardCopied &&
              <UrlCheckedImage
                src="https://res.kurly.com/mobile/service/goodsview/1804/ico_checked_x2.png"
                alt="URL 복사 아이콘"
              />
            }
          </LinkCopyButton>
        </ShareItemWrapper>
      </ShareItemList>
    </Container>
  );
}
